import React, { useMemo } from 'react';
import CookieHelper from '../../Helpers/cookieHelper';

const SKIES = ['day', 'dusk', 'mint', 'night'];
const MOTIONS = ['bob', 'bounce', 'float', 'trot'];
const SPARKS = ['star', 'heart', 'dot'];
const FACINGS = ['right', 'left'];
const PATHS = ['idle', 'cross'];
const PALETTES = {
    rainbow: ['#ff5b6b', '#ff9f43', '#ffd24d', '#4fcf7d', '#4db6ff', '#9a6dff'],
    sunset: ['#ffd24d', '#ff9f43', '#ff6f6b', '#ff5b9e', '#c86dd7', '#8a5cff'],
    ocean: ['#4fd1c5', '#38c0ff', '#4db6ff', '#5a8dee', '#7b6cf6', '#9a6dff'],
    candy: ['#ff8fc8', '#ff6fae', '#ffa9d6', '#c79cff', '#9a8cff', '#6db5ff'],
};

// Active when the user opted in (cookie) OR it's actually April 1st.
export function isUnicornActive()
{
    if (CookieHelper.getCookie('unicorns', 'false') === 'true') return true;
    const d = new Date();
    return d.getMonth() === 3 && d.getDate() === 1;
}

const pick = (a) => a[Math.floor(Math.random() * a.length)];

function rollTraits()
{
    const paletteKey = pick(Object.keys(PALETTES));
    return {
        sky: pick(SKIES),
        motion: pick(MOTIONS),
        spark: pick(SPARKS),
        facing: pick(FACINGS),
        path: pick(PATHS),
        palette: PALETTES[paletteKey],
    };
}

function Spark({ shape, x, y, s, delay, idx })
{
    const t = `translate(${x},${y})`;
    const style = { animationDelay: delay };
    const cls = 's s' + idx;
    if (shape === 'heart')
    {
        const d = `M0,${s * 0.3} C ${-s},${-s} ${-s * 1.7},${s * 0.5} 0,${s * 1.5} C ${s * 1.7},${s * 0.5} ${s},${-s} 0,${s * 0.3} Z`;
        return <path className={cls} style={style} transform={t} d={d} fill="#fff" />;
    }
    if (shape === 'dot') return <circle className={cls} style={style} transform={t} r={s * 0.7} fill="#fff" />;
    const b = s * 0.28;
    const d = `M0,${-s} L${b},${-b} L${s},0 L${b},${b} L0,${s} L${-b},${b} L${-s},0 L${-b},${-b} Z`;
    return <path className={cls} style={style} transform={t} d={d} fill="#fff" />;
}

function UnicornChar({ colors, motion })
{
    const navy = '#21364f';
    const mane = colors;
    return (
        <g className={'uni-char uni-m-' + motion}>
            <g className="uni-tail">
                <ellipse cx="16" cy="64" rx="11" ry="10" fill={mane[0]} />
                <ellipse cx="9" cy="76" rx="11" ry="10" fill={mane[1]} />
                <ellipse cx="8" cy="89" rx="11" ry="10" fill={mane[2]} />
                <ellipse cx="13" cy="101" rx="10" ry="10" fill={mane[3]} />
                <ellipse cx="21" cy="110" rx="9" ry="9" fill={mane[4]} />
            </g>
            <g stroke={navy} strokeWidth="4" strokeLinecap="round" fill="#fff">
                <rect x="30" y="96" width="11" height="26" rx="5" />
                <rect x="46" y="98" width="11" height="24" rx="5" />
                <rect x="66" y="98" width="11" height="24" rx="5" />
                <rect x="80" y="96" width="11" height="26" rx="5" />
            </g>
            <g fill={mane[5]}>
                <rect x="30" y="116" width="11" height="8" rx="3" />
                <rect x="46" y="114" width="11" height="8" rx="3" />
                <rect x="66" y="114" width="11" height="8" rx="3" />
                <rect x="80" y="116" width="11" height="8" rx="3" />
            </g>
            <ellipse cx="58" cy="80" rx="42" ry="33" fill="#fff" stroke={navy} strokeWidth="4" />
            <g className="uni-mane">
                <ellipse cx="82" cy="34" rx="12" ry="11" fill={mane[0]} />
                <ellipse cx="73" cy="42" rx="12" ry="11" fill={mane[1]} />
                <ellipse cx="66" cy="52" rx="12" ry="11" fill={mane[2]} />
                <ellipse cx="60" cy="63" rx="12" ry="11" fill={mane[3]} />
                <ellipse cx="56" cy="74" rx="12" ry="11" fill={mane[4]} />
                <ellipse cx="54" cy="85" rx="11" ry="10" fill={mane[5]} />
            </g>
            <ellipse cx="92" cy="54" rx="27" ry="24" fill="#fff" stroke={navy} strokeWidth="4" />
            <ellipse cx="113" cy="62" rx="13" ry="11" fill="#fff" stroke={navy} strokeWidth="4" />
            <path d="M78 34 L74 18 L88 30 Z" fill="#fff" stroke={navy} strokeWidth="3.5" strokeLinejoin="round" />
            <ellipse cx="92" cy="64" rx="6" ry="4" fill="#ffb3c1" opacity="0.85" />
            <circle cx="99" cy="52" r="3.4" fill={navy} />
            <circle cx="100.4" cy="50.8" r="1.1" fill="#fff" />
            <circle cx="119" cy="64" r="1.6" fill={navy} />
            <g className="uni-horn">
                <path d="M90 30 L98 -4 L103 30 Z" fill="url(#uniHorn)" stroke={navy} strokeWidth="2.4" strokeLinejoin="round" />
                <path d="M93 22 L101 24 M94 14 L100 16 M95.5 6 L99.5 8" stroke="#caa23a" strokeWidth="1.6" strokeLinecap="round" />
            </g>
            <ellipse className="uni-forelock" cx="86" cy="32" rx="9" ry="8" fill={mane[0]} />
        </g>
    );
}

export function UnicornScene()
{
    const traits = useMemo(() => rollTraits(), []);
    const sparkPos = [
        { x: 60, y: 150, s: 5, delay: '0s' },
        { x: 300, y: 120, s: 5, delay: '.4s' },
        { x: 280, y: 170, s: 4, delay: '.8s' },
        { x: 80, y: 96, s: 4, delay: '1.2s' },
    ];
    return (
        <span className={'uni-stage uni-sky-' + traits.sky} role="img" aria-label="A unicorn over a rainbow">
            <svg className="uni-svg" viewBox="0 0 360 220" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="uniHorn" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#fff0b8" />
                        <stop offset="1" stopColor="#f4b740" />
                    </linearGradient>
                </defs>
                <g className="uni-arc" fill="none" strokeLinecap="round">
                    <path d="M30 206 A150 150 0 0 1 330 206" stroke="#ff5b6b" strokeWidth="12" />
                    <path d="M42 206 A138 138 0 0 1 318 206" stroke="#ff9f43" strokeWidth="12" />
                    <path d="M54 206 A126 126 0 0 1 306 206" stroke="#ffd24d" strokeWidth="12" />
                    <path d="M66 206 A114 114 0 0 1 294 206" stroke="#4fcf7d" strokeWidth="12" />
                    <path d="M78 206 A102 102 0 0 1 282 206" stroke="#4db6ff" strokeWidth="12" />
                    <path d="M90 206 A90 90 0 0 1 270 206" stroke="#9a6dff" strokeWidth="12" />
                </g>
                <g className="uni-cloud c1" fill="#ffffff" opacity="0.92">
                    <ellipse cx="0" cy="46" rx="26" ry="15" />
                    <ellipse cx="22" cy="40" rx="20" ry="16" />
                    <ellipse cx="42" cy="48" rx="22" ry="13" />
                </g>
                <g className="uni-cloud c2" fill="#ffffff" opacity="0.8">
                    <ellipse cx="0" cy="40" rx="20" ry="12" />
                    <ellipse cx="18" cy="34" rx="16" ry="13" />
                    <ellipse cx="34" cy="41" rx="18" ry="11" />
                </g>
                <g className="uni-sparks">
                    {sparkPos.map((p, i) => <Spark key={i} shape={traits.spark} idx={i + 1} {...p} />)}
                </g>
                <g className={'uni-travel uni-path-' + traits.path + ' uni-face-' + traits.facing}>
                    <g transform="translate(120,58) scale(1.08)">
                        <g className={'uni-flip uni-face-' + traits.facing}>
                            <UnicornChar colors={traits.palette} motion={traits.motion} />
                        </g>
                    </g>
                </g>
            </svg>
        </span>
    );
}
